import { useEffect, useRef } from 'react'
import crypto from 'crypto-js'
import { notifyError, notifySuccess } from 'utils'
import { useVerifyPaymentMutation } from '../walletApiSlice'
import { CreateOrderResponse, PaymentError, PaymentStatus } from '../types'

const loadScript = (src: string): Promise<boolean> =>
  new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })

interface RenderRazorpayProps {
  orderId: string;
  keyId: string;
  keySecret: string;
  currency: string;
  amount: number;
  onSuccess: () => void;
}

const RenderRazorpay: React.FC<RenderRazorpayProps> = ({
  orderId,
  keyId,
  currency,
  amount,
  keySecret,
  onSuccess
}) => {
  const paymentId = useRef<string | null>(null)
  const paymentMethod = useRef<string | null>(null)
  const [VerifyPayment] = useVerifyPaymentMutation()
  useEffect(() => {
    displayRazorpay(options)
    // eslint-disable-next-line
  }, [])
  const displayRazorpay = async (options: any) => {
    const res = await loadScript('https://checkout.razorpay.com/v1/checkout.js')
    if (!res) {
      notifyError('Razorpay SDK failed to load. Are you online?', {
        containerId: 'modalToast'
      })

      return
    }

    const rzp1 = new (window as any).Razorpay(options)

    rzp1.on('payment.submit', (response: any) => {
      paymentMethod.current = response.method
    })

    rzp1.on('payment.failed', (response: any) => {
      paymentId.current = response.error.metadata.payment_id
    })

    rzp1.open()
  }

  const handlePayment = async (
    status: string,
    orderDetails?: { orderId?: string, paymentId?: string, signature?: string }
  ) => {
    if (orderDetails?.orderId && orderDetails?.paymentId) {
      const payload = {
        razorpayOrderId: orderDetails?.orderId,
        razorpayPaymentId: orderDetails?.paymentId,
        razorpaySignature: orderDetails?.signature ?? '',
        amount: amount.toString()
      }

      try {
        await VerifyPayment(payload).unwrap()
        onSuccess()
        notifySuccess('Payment Add Successfully')
      } catch (error) {
        notifyError('Error Occured Payment Verification', {
          containerId: 'modalToast'
        })
      }
    } else {
      notifyError('Invalide Order Details', {
        containerId: 'modalToast'
      })
    }
  }

  const options = {
    key: keyId,
    amount,
    currency,
    name: 'Multigenesys Software Pvt Ltd',
    order_id: orderId,
    handler: (response: CreateOrderResponse) => {
      paymentId.current = response.razorpay_payment_id ?? null
      const succeeded =
        crypto
          .HmacSHA256(`${orderId}|${response.razorpay_payment_id}`, keySecret)
          .toString() === response.razorpay_signature
      if (succeeded) {
        handlePayment(PaymentStatus.Succeeded, {
          orderId,
          paymentId: paymentId.current ?? '',
          signature: response.razorpay_signature ?? ''
        })
      } else {
        handlePayment(PaymentStatus.Failed, {
          orderId,
          paymentId: paymentId.current ?? ''
        })
      }
    },
    modal: {
      confirm_close: true,
      ondismiss: (reason: PaymentStatus | PaymentError | null) => {
        if (!reason) {
          handlePayment(PaymentStatus.Cancelled)
        } else if (reason === PaymentStatus.TimedOut) {
          handlePayment(PaymentStatus.TimedOut)
        } else {
          handlePayment(PaymentStatus.Failed, {
            orderId: (reason as PaymentError).error?.metadata?.order_id ?? '',
            paymentId:
              (reason as PaymentError).error?.metadata?.payment_id ?? ''
          })
        }
      }
    },
    retry: { enabled: false },
    timeout: 900,
    theme: { color: '#3399cc' }
  }

  return null
}

export default RenderRazorpay
